import { queueAutoPlay } from 'features/events/thunks';
import { addNewMessage, deleteMessageSuccess } from 'features/chat/chatSlice';
import {
  setCrowdList,
  addCrowdMember,
  removeCrowdMembers
} from 'features/crowd/crowdSlice';
import React, { createContext, useRef, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { RootState } from 'rootReducer';
import {
  setSocketId,
  setHubBroadcastConnected,
  setHubConnected,
  setHubCrowdConnected
} from './hubSlice';
import {
  updateEvent,
  fetchEvent,
  updateOrFetchEvent,
  addEvents,
  removeEvent
} from 'features/events/eventsSlice';
import { mapHubEventToEvent } from 'features/hub/stateMappers';
import {
  handleBroadcastFinished,
  handleEventStopped,
  handleListenerCountMessage,
  handleActionCreated
} from 'features/hub/hubThunks';
import { useSubdomain } from 'hooks/useSubdomain';
import { useNativeEvents } from 'hooks/useNativeEvents';
import { useCurrentUser } from 'features/current-user/hooks';
import { mapHubBroadcastStart } from 'features/broadcast/mappers';
import { addBroadcast } from 'features/broadcast/broadcastsSlice';
import { createAlert } from 'features/alert/alertSlice';
import { UserAgentContext } from 'features/user-agent/userAgent';

interface ContextProps {
  connectToSocket: () => void;
  disconnectFromSocket: () => void;
  subscribeToBroadcast: (value: string) => void;
  disconnectBroadcast: (value: string) => void;
  joinCrowd: () => void;
  leaveCrowd: () => void;
}

const WebSocketContext = createContext({} as ContextProps);

export { WebSocketContext };

export interface SocketMessage {
  channel: string;
  type: string;
  data: unknown;
}

export interface HubCommentCreatedMessage {
  id: number;
  content: string;
  name: string;
  user_id: string;
  user_slug: string;
  created_at: Date;
  image_url: string;
}

export interface HubCommentDeletedMessage {
  comment_id: number;
}

export interface HubBroadcastStart {
  broadcast: HubBroadcastState;
}

export interface HubBroadcastState {
  uid: string;
  title: string;
  duration: number | null;
  category_name: string;
  streams: HubBroadcastStreams;
  listener_count: number;
  heart_count?: number;
  started_at: string;
  event_id: string | null;
}

interface HubBroadcastStreams {
  progressive: HubBroadcastStreamUrl;
}

interface HubBroadcastStreamUrl {
  url: string;
}

export interface HubCallback {
  id: string;
}

export interface HubMember {
  id: string;
  user: {
    id: string;
    username: string;
    profile_image_url: string;
  };
}

export interface HubActionCreated {
  total: number;
  broadcast_uid: string;
  username: string;
  user_id: number;
  created_at: string;
}

export interface HubActionHeartCreated {
  display_name: string;
  user_id: number;
  created_at: string;
}

export interface HubListenerCount {
  count: number;
}

export interface HubEventPayload {
  id: number;
  starts_at: string;
  ends_at: string;
  started_at: string;
  ended_at: string;
  active: boolean;
  title: string;
  description: string;
  artwork: Record<string, never> | { url: string; original_key: string };
  artwork_mode: string;
  timezone: string;
  adhoc: boolean | null;
  accesscode_enabled: boolean | null;
  chat_enabled: boolean | null;
  hearting_enabled: boolean | null;
  stats_visible: boolean | null;
  broadcaster: {
    id: number;
  };
  url: string;
  updated_at: string;
}

export interface HubEventMessage {
  event: HubEventPayload;
}

// We declare the socket here outside the scope of the component
// so that with StrictMode 2 sockets aren't created
let socket: ReconnectingWebSocket | null = null;

interface HubProps {
  children: React.ReactNode;
}

export const WebsocketProvider = ({ children }: HubProps): React.ReactNode => {
  const channelId = useSelector(
    (state: RootState) => state.channel.channelDetails?.ownerId
  );

  const hubServer = useSelector((state: RootState) => state.hub.hubServer);
  const dispatch = useDispatch();
  const subdomain = useSubdomain();
  const { postMessage } = useNativeEvents();
  const currentUser = useCurrentUser();
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout>();
  const { isDACreatorApp } = useContext(UserAgentContext);

  const connectToSocket = () => {
    // We have not got the hub server from the load balancer yet
    if (!hubServer || !channelId) {
      return;
    }

    if (!socket) {
      console.log('hubServer', hubServer);
      socket = new ReconnectingWebSocket(hubServer);

      const userChannel = `${process.env.REACT_APP_HUB_ENV};user;${channelId}`;

      socket.onopen = function ({ target }) {
        console.log('[open] Connection established');
        socket = target as ReconnectingWebSocket;
        connectToHub();
        sendHeartbeat();
        dispatch(setHubConnected(true));
        console.log('env ', process.env.REACT_APP_HUB_ENV);
        socket.send(
          JSON.stringify({
            type: 'hubSubscribe',
            channel: userChannel
          })
        );
      };

      socket.onmessage = function (e) {
        const data = JSON.parse(e.data)[0];
        const jsonData = data as SocketMessage;
        switch (jsonData.type) {
          case 'comment:created': {
            if (jsonData.channel !== userChannel) {
              break;
            }
            console.log('[hub] Comment created', jsonData.data);
            const comment = jsonData.data as HubCommentCreatedMessage;
            dispatch(addNewMessage(comment));
            break;
          }
          case 'comment:deleted': {
            console.log('[hub] Comment deleted', jsonData.data);
            const { comment_id } = jsonData.data as HubCommentDeletedMessage;
            dispatch(deleteMessageSuccess(comment_id.toString()));
            break;
          }
          case 'broadcast:start': {
            console.log('[hub] Broadcast Start', jsonData.data);
            const broadcastData = data.data as HubBroadcastStart;
            postMessage('broadcastStarted');
            dispatch(addBroadcast(mapHubBroadcastStart(broadcastData)));
            break;
          }
          case 'listener_count':
            console.log('[hub] Listener Count', jsonData.data);
            dispatch(handleListenerCountMessage(data));
            break;
          case 'action:created':
            console.log('[hub] Action Created', jsonData.data);
            dispatch(handleActionCreated(data));
            break;
          case 'member:list': {
            console.log('[hub] Member List', jsonData.data);
            const members = jsonData.data as HubMember[];
            dispatch(setCrowdList(members));
            break;
          }
          case 'member:added': {
            console.log('[hub] Member Added', jsonData.data);
            const member = jsonData.data as HubMember[];
            dispatch(addCrowdMember(member));
            break;
          }
          case 'member:removed': {
            console.log('[hub] Member Removed', jsonData.data);
            const memberArray = jsonData.data as Array<string>;
            dispatch(removeCrowdMembers(memberArray));
            break;
          }
          case 'event:started': {
            console.log('[hub] Event started', jsonData.data);
            const message = jsonData.data as HubEventMessage;
            dispatch(
              updateOrFetchEvent(subdomain, message.event as HubEventPayload)
            );
            dispatch(queueAutoPlay(message));
            break;
          }
          case 'event:updated': {
            console.log('[hub] Event Updated', jsonData.data);
            const message = jsonData.data as HubEventMessage;
            dispatch(updateEvent(mapHubEventToEvent(message.event)));
            postMessage({ eventTitle: message.event.title });
            if (isDACreatorApp) {
              dispatch(
                createAlert({ message: 'Event updated', type: 'success' })
              );
            }
            break;
          }
          case 'event:created': {
            console.log('[hub] Event Created', jsonData.data);
            const message = jsonData.data as HubEventMessage;
            const event = mapHubEventToEvent(message.event);
            if (event.accesscodeEnabled) {
              dispatch(fetchEvent(subdomain, event.id));
            } else {
              dispatch(addEvents([event]));
            }
            break;
          }
          case 'event:past': {
            console.log('[hub] Event Past', jsonData.data);
            const message = jsonData.data as HubEventMessage;
            dispatch(removeEvent(message.event.id.toString()));
            break;
          }
          case 'event:destroyed': {
            console.log('[hub] Event Destroyed', jsonData.data);
            const message = jsonData.data as HubEventMessage;
            dispatch(removeEvent(message.event.id.toString()));
            break;
          }
          case 'event:stopped': {
            console.log('[hub] Event Stopped', jsonData.data);
            dispatch(handleEventStopped(jsonData.data as HubEventMessage));
            break;
          }
          case 'finished':
            console.log('[hub] Broadcast Finished', jsonData.data);
            dispatch(handleBroadcastFinished(data, disconnectBroadcast));
            break;
          case 'callback':
            console.log('[hub] Callback', jsonData.data);
            dispatch(setSocketId(jsonData.data as HubCallback));
            break;
          default:
            console.log(`Unknown message type: ${jsonData.type}`);
        }
      };

      socket.onclose = function (event) {
        dispatch(setHubConnected(false));
        dispatch(setHubBroadcastConnected(false));
        dispatch(setHubCrowdConnected(false));
        if (event.wasClean) {
          console.log(
            `[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`
          );
        } else {
          // e.g. server process killed or network down
          // event.code is usually 1006 in this case
          console.log('[close] Connection died');
        }
      };
    }
  };

  const disconnectFromSocket = () => {
    if (socket) {
      socket.close();
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
    }
  };

  const connectToHub = () => {
    let auth = {};

    if (currentUser) {
      auth = {
        id: currentUser.id,
        user: {
          id: currentUser.id,
          username: currentUser.username,
          profile_image_url: currentUser.profile_image_url
        }
      };
    }

    if (socket?.readyState === WebSocket.OPEN) {
      console.log('[Hub] - Connecting to hub');
      socket.send(JSON.stringify({ type: 'hubConnect', data: auth }));
    }
  };

  const sendHeartbeat = () => {
    console.log('[Hub] - Starting heartbeat');
    heartbeatTimeoutRef.current = setTimeout(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'hubHeartbeat'
          })
        );
      }
    }, 20000);
  };

  // Subscribe to Broadcast Channel when broadcast is started
  const subscribeToBroadcast = (broadcastUID: string) => {
    if (socket?.readyState === WebSocket.OPEN) {
      console.log('[Hub] - Subscribing to broadcast');
      socket.send(
        JSON.stringify({
          type: 'hubSubscribe',
          channel: `${process.env.REACT_APP_HUB_ENV};broadcast;${broadcastUID}`
        })
      );
      dispatch(setHubBroadcastConnected(true));
      console.log(`[broadcast] Connected Broadcast UID: ${broadcastUID}`);
    } else {
      console.log('[broadcast] Connection not open - not connecting broadcast');
    }
  };

  const disconnectBroadcast = (broadcastUID: string) => {
    if (socket?.readyState === WebSocket.OPEN) {
      console.log('[Hub] - Unsubscribing broadcast');
      socket.send(
        JSON.stringify({
          type: 'hubUnsubscribe',
          channel: `${process.env.REACT_APP_HUB_ENV};broadcast;${broadcastUID}`
        })
      );
      dispatch(setHubBroadcastConnected(false));
      console.log(`[broadcast] Unsubscribing broadcast: ${broadcastUID}`);
    } else {
      console.log('[broadcast] Connection not open - not connecting broadcast');
    }
  };

  const joinCrowd = () => {
    connectToHub();
    if (socket?.readyState === WebSocket.OPEN) {
      console.log('[Hub] - Subscribing to crowd');
      socket.send(
        JSON.stringify({
          type: 'hubSubscribe',
          channel: `presence-${process.env.REACT_APP_HUB_ENV};crowd;${channelId}`
        })
      );
      dispatch(setHubCrowdConnected(true));
      console.log(`[Hub] - Connected crowd ID: ${channelId}`);
    } else {
      console.log('[broadcast] Connection not open - not connecting crowd');
    }
  };

  const leaveCrowd = () => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'hubUnsubscribe',
          channel: `presence-${process.env.REACT_APP_HUB_ENV};crowd;${channelId}`
        })
      );
      dispatch(setHubCrowdConnected(false));
      console.log(`[Hub] - Unsubscribing from crowd: ${channelId}`);
    } else {
      console.log('[crowd] Connection not open - not connecting crowd');
    }
  };

  return (
    <WebSocketContext.Provider
      value={{
        connectToSocket,
        disconnectFromSocket,
        subscribeToBroadcast,
        disconnectBroadcast,
        joinCrowd,
        leaveCrowd
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
